<template>
  <div class="modal fade" tabindex="-1" role="dialog" aria-hidden="true">
    <!-- MODAL DIALOG -->
    <div
      class="modal-dialog modal-dialog-centered"
      :class="showConfirmationWindow ? 'modal-lg' : 'modal-xxl'"
      role="document"
    >
      <!-- ROW -->
      <div class="row ml-0 w-100 h-100">
        <!-- COLUMN -->
        <div :class="[showConfirmationWindow ? 'col-12' : 'col-lg-8 col-md-12', { 'p-0': $breakpoint.mdAndDown }]">
          <!-- MODAL CONTENT -->
          <div class="modal-content responsive-pb-0">
            <div class="modal-header">
              <h1 class="modal-title mt-0 heading-large">
                <span v-if="!getEditingStatus">Create</span>
                <span v-else>Edit</span>
                <span v-if="isContractOffer && serviceSelected === 'SPOT'"> Ex-Rack Contract </span>
                <span v-else-if="isContractOffer && serviceSelected === 'BUNKERING'"> Bunkering Contract </span>
                <span> Offer </span>
              </h1>
              <button
                v-if="showConfirmationWindow || $breakpoint.mdAndDown"
                type="button"
                data-dismiss="modal"
                class="modal-close"
                @click="closeModal(showConfirmationWindow)"
                aria-label="Cancel"
              >
                <i class="fas fa-lg fa-times"></i>
              </button>
            </div>

            <!-- Offer Setup Window -->
            <div v-if="!showConfirmationWindow">
              <!-- MODAL BODY -->
              <div class="modal-body">
                <el-form
                  :model="offer"
                  :rules="formRules"
                  ref="ruleForm"
                  size="mini"
                  label-width="120px"
                  :hide-required-asterisk="true"
                  label-position="top"
                >
                  <!-- Display a message when seller has not entered daily settlement prices -->
                  <div v-if="!hasDailySettlementPricesBeenSet" class="row mx-0 mb-4">
                    <div class="col-md-12 px-0" :class="{ 'no-results d-flex': $breakpoint.mdAndDown }">
                      <div class="w-100 alert tables-alert-custom-message-notify my-0 text-center">
                        <i class="fas fa-info-circle mr-2"></i>
                        Offers will not be displayed to buyers until Settlement Prices have been updated
                      </div>
                    </div>
                  </div>

                  <!-- Select a service type if this is creating offer -->
                  <div class="group-rounded-box mt-0" v-if="!getEditingStatus">
                    <span class="title">Channel</span>

                    <div class="row">
                      <div class="col-md-12 text-center mb-3">
                        <h5 class="my-3">Please select a channel you prefer</h5>
                        <span v-for="(service, key) in serviceTypeOptions" :key="key">
                          <button
                            type="button"
                            class="btn btn-outline-t_grey mr-3"
                            :class="{ active: serviceSelected === service }"
                            @click="chooseServiceType(service)"
                          >
                            {{ service | offerservicetype }}
                          </button>
                        </span>
                      </div>
                    </div>
                  </div>

                  <!-- DETAILS BOX -->
                  <div
                    class="group-rounded-box row-py-clear"
                    :class="{ 'mt-0': getEditingStatus }"
                    v-if="serviceSelected"
                  >
                    <span class="title">Details</span>

                    <!-- CONTRACT DETAILS -->
                    <template v-if="showIntegrationDetails">
                      <!-- ROW -->
                      <div class="row">
                        <!-- COLUMN -->
                        <div v-if="getBrands.length > 0" class="col-md-6">
                          <!-- FORM GROUP -->
                          <el-form-item
                            label="Brand"
                            prop="meta.integration.brand"
                            v-if="offer.meta && offer.meta.integration"
                          >
                            <el-select
                              v-model="offer.meta.integration.brand"
                              :disabled="getEditingStatus"
                              placeholder="Select a brand"
                              value-key="name"
                              filterable
                            >
                              <el-option
                                v-for="(brand, key) in getBrands"
                                :value="brand"
                                :label="brand.name"
                                :key="key"
                              ></el-option>
                            </el-select>
                          </el-form-item>
                        </div>

                        <!-- COLUMN -->
                        <div class="col-md-6">
                          <!-- FORM GROUP -->
                          <el-form-item
                            v-if="offer.meta && offer.meta.integration"
                            label="External Contract ID"
                            prop="meta.integration.externalContractId"
                          >
                            <el-input
                              v-model="offer.meta.integration.externalContractId"
                              :disabled="getEditingStatus"
                              placeholder="Enter External Contract ID"
                            >
                            </el-input>
                          </el-form-item>
                        </div>
                      </div>
                    </template>

                    <!-- ROW -->
                    <div class="row" v-if="isContractOffer">
                      <!-- COLUMN -->
                      <div class="col-md-6">
                        <!-- FORM GROUP -->
                        <el-form-item v-if="offer.contracts" label="Buyer" prop="contracts.buyer">
                          <el-select
                            v-model="offer.contracts.buyer"
                            :disabled="getEditingStatus"
                            placeholder="Select a buyer"
                            value-key="email"
                            @change="refreshRules"
                            filterable
                          >
                            <el-option
                              v-for="(buyer, key) in buyersList"
                              :value="buyer"
                              :label="buyer.name"
                              :key="key"
                            ></el-option>
                          </el-select>
                        </el-form-item>
                        <!-- FORM GROUP -->
                        <!-- COLUMN -->
                      </div>

                      <!-- COLUMN -->
                      <div class="col-md-6">
                        <!-- FORM GROUP -->
                        <el-form-item v-if="offer.contracts" label="Contract Reference" prop="contracts.reference">
                          <el-input v-model="offer.contracts.reference" placeholder="Enter a contract reference">
                          </el-input>
                        </el-form-item>
                        <!-- FORM GROUP -->
                        <!-- COLUMN -->
                      </div>
                      <!-- ROW -->
                    </div>

                    <!-- ROW -->
                    <div class="row">
                      <!-- COLUMN -->
                      <div class="col-md-6">
                        <!-- FORM GROUP -->
                        <el-form-item :label="showProductOrNetwork()" prop="fuelGroupSelected">
                          <el-select
                            v-model="fuelGroupSelected"
                            :disabled="getEditingStatus"
                            :placeholder="`Select a ${showProductOrNetwork().toLowerCase()}`"
                            value-key="_id"
                            @change="refreshRules"
                          >
                            <el-option
                              v-for="(fuel, key) in fuelsList"
                              :value="fuel"
                              :label="fuel.name"
                              :key="key"
                            ></el-option>
                          </el-select>
                        </el-form-item>
                        <!-- FORM GROUP -->
                        <!-- COLUMN -->
                      </div>

                      <!-- COLUMN -->
                      <div class="col-md-6" v-if="serviceSelected === 'SPOT'">
                        <!-- FORM GROUP -->
                        <el-form-item label="Terminal" prop="terminal">
                          <el-select
                            v-model="offer.terminal"
                            :disabled="getEditingStatus"
                            @change="refreshRules"
                            placeholder="Search terminal here..."
                            value-key="_id"
                            filterable
                          >
                            <el-option
                              v-for="(terminal, key) in getTerminalChanges"
                              :value="terminal"
                              :label="terminal.name"
                              :key="key"
                            ></el-option>
                          </el-select>
                        </el-form-item>
                        <!-- FORM GROUP -->
                        <!-- COLUMN -->
                      </div>
                      <!-- ROW -->
                    </div>

                    <!-- ROW -->
                    <div class="row">
                      <!-- COLUMN -->
                      <div class="col-md-6">
                        <!-- FORM GROUP -->
                        <el-form-item label="Bio Pricing" prop="fuel">
                          <el-select
                            v-model="offer.fuel"
                            :disabled="getEditingStatus || !showBioOptions"
                            @change="refreshRules"
                            placeholder="Select a bio pricing"
                            value-key="_id"
                            filterable
                          >
                            <el-option
                              v-for="(price, key) in bioPricingOptions"
                              :value="price"
                              :label="key == 0 ? 'Straight' : 'Split Quote'"
                              :key="key"
                            ></el-option>
                          </el-select>
                        </el-form-item>
                        <!-- FORM GROUP -->
                        <!-- COLUMN -->
                      </div>

                      <!-- COLUMN -->
                      <div v-if="!isContractOffer" class="col-md-3">
                        <el-form-item label="Volume Min/L" prop="minimumOrderQuantity">
                          <el-input v-cleave="naturalNumber" v-model="offer.minimumOrderQuantity" placeholder="0">
                          </el-input>
                        </el-form-item>
                        <!-- FORM GROUP -->
                        <!-- COLUMN -->
                      </div>

                      <!-- COLUMN -->
                      <div v-if="!isContractOffer" class="col-md-3">
                        <el-form-item label="Volume Max/L" prop="maximumAvailable">
                          <el-input
                            v-cleave="naturalNumber"
                            v-model="offer.maximumAvailable"
                            placeholder="0"
                            @input.native="changeRemainingVolume(false)"
                          >
                          </el-input>
                        </el-form-item>
                        <!-- FORM GROUP -->
                        <!-- COLUMN -->
                      </div>
                      <!-- ROW -->
                    </div>
                    <br />

                    <!-- ROW -->
                    <div class="row" v-if="serviceSelected == 'SPOT'">
                      <!-- COLUMN -->
                      <div class="col-md-6">
                        <el-form-item label="Lifting Period" prop="collectionTime">
                          <el-select
                            v-model="offer.collectionTime"
                            :disabled="offer.pricingMethod == 'D1' || offer.pricingMethod == 'D2'"
                            placeholder="Select a lifting period"
                            filterable
                          >
                            <el-option
                              v-for="(period, key) in collectionPeriodOptions"
                              :value="period"
                              :label="period === -1 ? 'Input Lifting Period' : period | collectiondaysformat"
                              :key="key"
                            ></el-option>
                          </el-select>
                        </el-form-item>
                        <!-- FORM GROUP -->
                      </div>

                      <!-- COLUMN -->
                      <div class="col-md-6">
                        <div class="form-group">
                          <label>
                            Lifting Start Date - Lifting End Date
                            <i
                              class="fas fa-info-circle"
                              data-toggle="tooltip"
                              data-placement="top"
                              title="This Lifting Period will update automatically each day. All Lifting Periods are based on calendar days."
                            ></i>
                          </label>
                          <el-form-item v-if="offer.collectionTime === -1" prop="liftingPeriod" required>
                            <el-date-picker
                              v-model="offer.liftingPeriod"
                              :picker-options="pickerOptions"
                              type="daterange"
                              range-separator="-"
                              start-placeholder="Start date"
                              end-placeholder="End date"
                            >
                            </el-date-picker>
                          </el-form-item>
                          <span v-else class="d-flex text-center my-2">{{
                            offer.collectionTime | collectiondaysformatdates
                          }}</span>
                        </div>
                      </div>
                      <!-- ROW -->
                    </div>

                    <!-- ROW -->
                    <div class="row">
                      <!-- COLUMN -->
                      <div class="col-md-6">
                        <!-- FORM GROUP -->
                        <el-form-item label="Pricing Method" prop="pricingMethod">
                          <el-select
                            v-model="offer.pricingMethod"
                            :disabled="(getEditingStatus && isContractOffer) || !hasDailySettlementPricesBeenSet"
                            placeholder="Select a pricing method"
                          >
                            <el-option
                              v-for="(method, key) in pricingMethodOptions"
                              :value="method"
                              :label="method | offerpricingmethod"
                              :key="key"
                            ></el-option>
                          </el-select>
                        </el-form-item>
                        <!-- COLUMN -->
                      </div>

                      <!-- COLUMN -->
                      <div class="col-md-6">
                        <!-- FORM GROUP -->
                        <el-form-item label="Measurement" prop="fuel_measurement_id">
                          <el-select
                            v-model="offer.fuel_measurement_id"
                            :disabled="getEditingStatus && isContractOffer"
                            @change="refreshRules"
                            placeholder="Select a measurement type"
                          >
                            <el-option
                              v-for="(measurement, key) in fuelMeasurementOptions"
                              :value="measurement"
                              :label="measurement | fuelmeasurementtype"
                              :key="key"
                            ></el-option>
                          </el-select>
                        </el-form-item>
                        <!-- COLUMN -->
                      </div>
                      <!-- ROW -->
                    </div>

                    <!-- ROW -->
                    <div class="row">
                      <!-- COLUMN -->
                      <div class="col-md-6">
                        <el-form-item label="Price Input" prop="priceInputType">
                          <el-select
                            v-model="offer.priceInputType"
                            :disabled="
                              offer.pricingMethod == 'FIXED' ||
                              offer.pricingMethod == 'SETTLEMENT' ||
                              (getEditingStatus && isContractOffer)
                            "
                            @change="refreshRules"
                            placeholder="Select a price type"
                          >
                            <el-option
                              v-for="(priceInputType, key) in priceInputTypeOptions"
                              :value="priceInputType"
                              :label="$root.$options.filters.offerpriceinputtype(priceInputType, usePricingEngine)"
                              :key="key"
                            ></el-option>
                          </el-select>
                        </el-form-item>
                        <!-- FORM GROUP -->
                        <!-- COLUMN -->
                      </div>
                      <!-- COLUMN -->
                      <div v-if="!(usePricingEngine && offer.priceInputType === 'PREMIUM')" class="col-md-6">
                        <el-form-item prop="premium" v-if="offer.priceInputType == 'PREMIUM'">
                          <span slot="label">{{ offer.priceInputType | offerpriceinputtype }} </span>
                          <el-input
                            :disabled="!hasDailySettlementPricesBeenSet || (getEditingStatus && isContractOffer)"
                            v-cleave="numeral"
                            v-model="offer.premium"
                            placeholder="0.00"
                            @blur="validateOfferInput(offerInputIds.Premium)"
                          >
                          </el-input>
                        </el-form-item>
                        <el-form-item prop="totalPrice" v-else>
                          <span slot="label">{{ offer.priceInputType | offerpriceinputtype }} </span>
                          <el-input
                            :disabled="!hasDailySettlementPricesBeenSet || (getEditingStatus && isContractOffer)"
                            v-cleave="numeralPositive"
                            v-model="offer.totalPrice"
                            placeholder="0.00"
                            @blur="validateOfferInput(offerInputIds.TotalPrice)"
                          >
                          </el-input>
                        </el-form-item>
                        <!-- FORM GROUP -->
                        <!-- COLUMN -->
                      </div>

                      <!-- COLUMN -->
                      <div v-if="isContractOffer" class="col-md-6">
                        <!-- FORM GROUP -->
                        <!-- PAYMENT TERM OVERRIDE CHECKBOX -->
                        <input
                          type="checkbox"
                          v-model="isPaymentTermOverridden"
                          class="mr-2"
                          :disabled="getEditingStatus"
                        />
                        <label>Payment Term Override (Optional)</label>

                        <!-- PAYMENT TERM OVERRIDE INPUT -->
                        <div class="d-flex">
                          <el-form-item prop="contracts.paymentTermOverride">
                            <el-input
                              :disabled="getEditingStatus || !isPaymentTermOverridden"
                              v-cleave="naturalNumber"
                              v-model="offer.contracts.paymentTermOverride"
                              placeholder="Enter a payment term"
                            >
                            </el-input>
                          </el-form-item>
                          <span class="px-2 pt-3">Day(s)</span>
                        </div>
                        <!-- FORM GROUP -->
                        <!-- COLUMN -->
                      </div>
                      <!-- ROW -->
                    </div>
                    <!-- DETAILS BOX -->
                  </div>

                  <!-- CONTRACT SECTION -->
                  <div v-if="isContractOffer && serviceSelected" class="group-rounded-box row-py-clear">
                    <span class="title">Contract Breakdown</span>

                    <!-- ROW -->
                    <div class="row gx-5">
                      <!-- COLUMN -->
                      <div class="col-md-6">
                        <el-form-item label="Frequency" prop="contracts.frequency">
                          <el-select
                            v-model="offer.contracts.frequency"
                            :disabled="getEditingStatus"
                            placeholder="Select a frequency"
                          >
                            <el-option
                              v-for="(frequency, key) in frequencyOptions"
                              :value="frequency"
                              :label="frequency | contractfrequencytype"
                              :key="key"
                            ></el-option>
                          </el-select>
                        </el-form-item>
                        <!-- FORM GROUP -->
                        <!-- COLUMN -->
                      </div>

                      <!-- COLUMN -->
                      <div class="col-md-3">
                        <el-form-item label="Contract Start Date" prop="contracts.dates.start" class="pr-2">
                          <el-date-picker
                            :picker-options="pickerStartOptions"
                            :disabled="getEditingStatus"
                            v-model="offer.contracts.dates.start"
                            type="date"
                            style="width: 180px"
                            format="dd/MM/yyyy"
                            value-format="dd/MM/yyyy"
                            placeholder="Select a start date"
                          >
                          </el-date-picker>
                        </el-form-item>
                        <!-- FORM GROUP -->
                        <!-- COLUMN -->
                      </div>

                      <!-- COLUMN -->
                      <div class="col-md-3">
                        <el-form-item label="Contract End Date" prop="contracts.dates.end" class="pr-2">
                          <el-date-picker
                            :picker-options="pickerEndOptions"
                            v-model="offer.contracts.dates.end"
                            type="date"
                            style="width: 180px"
                            format="dd/MM/yyyy"
                            value-format="dd/MM/yyyy"
                            placeholder="Select a end date"
                          >
                          </el-date-picker>
                        </el-form-item>
                        <!-- FORM GROUP -->
                        <!-- COLUMN -->
                      </div>
                      <!-- ROW -->
                    </div>

                    <!-- ROW -->
                    <div
                      v-if="
                        offer.contracts.dates.start &&
                        offer.contracts.dates.end &&
                        offer.contracts.frequency === 'MONTHLY' &&
                        offer.contracts.frequencyQuantity &&
                        offer.contracts.frequencyQuantity.length > 0
                      "
                      class="row"
                    >
                      <!-- COLUMN -->
                      <div class="col-md-6">
                        <!-- FORM GROUP -->
                        <!-- <div class="form-group"> -->
                        <!-- CONTRACT BREAKDOWN PER MONTH -->
                        <div class="pb-2">
                          <label>Enter breakdown per month</label>
                        </div>
                        <div v-for="(frequencyQuantity, index) in offer.contracts.frequencyQuantity" :key="index">
                          <el-form-item :prop="'contracts.frequencyQuantity.' + index + '.quantity'">
                            <el-row :gutter="20">
                              <el-col :span="12"
                                >{{ (frequencyQuantity.untilDate.getMonth() + 1) | formatMonth }}
                                {{ frequencyQuantity.untilDate.getFullYear() }}</el-col
                              >
                              <el-col :span="12">
                                <el-input
                                  :disabled="monthIsDisabled(frequencyQuantity.untilDate) && getEditingStatus"
                                  v-cleave="naturalNumber"
                                  v-model="frequencyQuantity.quantity"
                                  placeholder="Enter Volume"
                                  @input.native="formatValue($event, index)"
                                >
                                </el-input>
                              </el-col>
                            </el-row>
                          </el-form-item>
                        </div>
                        <!-- FORM GROUP -->
                        <!-- </div> -->
                        <!-- COLUMN -->
                      </div>
                      <!-- ROW -->
                    </div>

                    <!-- ROW -->
                    <div v-if="isContractOffer" class="row">
                      <!-- COLUMN -->
                      <div class="col-md-6">
                        <el-form-item label="Min Vol per order (L)" prop="minimumOrderQuantity">
                          <el-input v-cleave="naturalNumber" v-model="offer.minimumOrderQuantity" placeholder="0">
                          </el-input>
                        </el-form-item>
                        <!-- FORM GROUP -->
                        <!-- COLUMN -->
                      </div>

                      <!-- COLUMN -->
                      <div class="col-md-6" v-if="frequencyQtyTotalVol">
                        <el-form-item label="Total contract Volume (L)">
                          <el-input
                            @input.native="changeRemainingVolume(true, $event.target.value)"
                            v-cleave="naturalNumber"
                            v-model="offer.contracts.totalQuantity"
                            placeholder="0"
                            :disabled="offer.contracts.frequency === 'MONTHLY' || getEditingStatus"
                          >
                          </el-input>
                        </el-form-item>
                        <!-- FORM GROUP -->
                        <!-- COLUMN -->
                      </div>
                      <!-- ROW -->
                    </div>

                    <!-- CONTRACT SECTION -->
                  </div>
                  <!-- SHOW COST ADDITIONS BREAKDOWN ONLY WHEN PRICING ENGINE IS USED AND OFFER IS NOT FIXED/SET AT FINAL PRICE -->
                  <template
                    v-if="
                      usePricingEngine &&
                      offer.pricingMethod !== 'FIXED' &&
                      offer.priceInputType != 'TOTAL' &&
                      serviceSelected
                    "
                  >
                    <p v-if="!(getEditingStatus && isContractOffer)">
                      <i class="fas fa-exclamation-circle mr-2"></i>You can adjust your global rules below by adding new
                      rules, removing rules, or adjusting their value. These changes will only apply to this specific
                      offer and will not affect your other offers.
                    </p>

                    <div class="mt-3 w-100 alert tables-alert-custom-message-notify text-center" v-if="loadingRuleData">
                      <span class="spinner-border spinner-border-sm mr-2 mb-1" role="status"></span> Loading rules...
                    </div>
                    <div v-else>
                      <!-- COST ADDITIONS -->
                      <cost-additions
                        ref="cumulativeRuleGroupGlobal"
                        v-if="
                          isCumulativeRuleGroupRelevantForOffer(
                            costAdditionScopeGetter,
                            componentMetadata.costAdditionsGlobal.componentKey,
                            offer,
                            blendedProductConfig
                          )
                        "
                        :key="`${componentMetadata.costAdditionsGlobal.componentKey}_${refreshKey}`"
                        :scope="costAdditionScopeGetter.Global"
                        :pricingRules.sync="componentMetadata.costAdditionsGlobal.rules"
                        :priority="rulesEnum.ePriority.Offer"
                        :title="componentMetadata.costAdditionsGlobal.title"
                        :originalPricingRulesList="originalCostAdditionRules"
                        :isRulesModificationAllowed="!(getEditingStatus && isContractOffer)"
                        :isInOfferModal="true"
                        :isContractOffer="isContractOffer"
                      />

                      <!-- BIO RULES -->
                      <cost-additions
                        ref="cumulativeRuleGroupBio"
                        v-if="
                          isCumulativeRuleGroupRelevantForOffer(
                            costAdditionScopeGetter,
                            componentMetadata.costAdditionsBio.componentKey,
                            offer,
                            blendedProductConfig
                          )
                        "
                        :key="`${componentMetadata.costAdditionsBio.componentKey}_${refreshKey}`"
                        :scope="costAdditionScopeGetter.Bio"
                        :pricingRules.sync="componentMetadata.costAdditionsBio.rules"
                        :priority="rulesEnum.ePriority.Offer"
                        :title="componentMetadata.costAdditionsBio.title"
                        :originalPricingRulesList="originalBioRules"
                        :isRulesModificationAllowed="!(getEditingStatus && isContractOffer)"
                        :isInOfferModal="true"
                        :isContractOffer="isContractOffer"
                      />

                      <!-- TEMPERATURE COST ADDITIONS -->
                      <cost-additions
                        ref="cumulativeRuleGroupTemperature"
                        v-if="
                          isCumulativeRuleGroupRelevantForOffer(
                            costAdditionScopeGetter,
                            componentMetadata.costAdditionsTemperature.componentKey,
                            offer,
                            blendedProductConfig
                          )
                        "
                        :key="`${componentMetadata.costAdditionsTemperature.componentKey}_${refreshKey}`"
                        :scope="costAdditionScopeGetter.Temperature"
                        :pricingRules.sync="componentMetadata.costAdditionsTemperature.rules"
                        :priority="rulesEnum.ePriority.Offer"
                        :originalPricingRulesList="originalTempRules"
                        :title="componentMetadata.costAdditionsTemperature.title"
                        :isRulesModificationAllowed="!(getEditingStatus && isContractOffer)"
                        :isInOfferModal="true"
                        :readOnly="!isContractOffer"
                        :isContractOffer="isContractOffer"
                      />
                    </div>
                  </template>
                  <!-- MODAL BODY -->
                </el-form>
              </div>
              <!-- Offer Setup Window -->
            </div>

            <!-- Offer Confirmation Window -->
            <div v-else>
              <div class="modal-body confirmation-window text-center">
                <h1>{{ confirmationMessage }}</h1>
                <i
                  class="fas fa-5x d-block my-5"
                  :class="offerPlaced ? 'fa-check-circle text-t_green' : ['fa-times-circle', 'text-t_red']"
                ></i>
                <button class="btn btn-neutral" data-dismiss="modal" @click="closeModal(true)" aria-label="Close">
                  Close
                </button>
              </div>
              <!-- Offer Confirmation Window -->
            </div>
            <!-- MODAL CONTENT -->
          </div>
          <!-- COLUMN -->
        </div>

        <!-- COLUMN -->
        <div v-if="!showConfirmationWindow" class="col-lg-4 col-md-12" :class="{ 'p-0': $breakpoint.mdAndDown }">
          <!-- PRICE BREAKDOWN -->
          <price-breakdown
            v-if="!showConfirmationWindow"
            class="w-100"
            :class="{ 'sticky-top': !$breakpoint.mdAndDown }"
            :offer="offer"
            :btnLoading="saveOfferBtnLoading"
            :rules="componentMetadata.priceBreakdown.rules"
            :allRulesValid="!usePricingEngine || offer.pricingMethod === 'FIXED' || offer.priceInputType === 'TOTAL'"
            :isEditing="getEditingStatus"
            :isSettlementSet="hasDailySettlementPricesBeenSet"
            :isFormComplete="
              errorsFound.length == 0 && serviceSelected != null && serviceSelected !== '' && !loadingRuleData
            "
            :detailsChanged="detailsChanged"
            :errors="relevantErrors"
            @save-offer="validateForm"
            @close-modal="closeModal"
          />
          <!-- COLUMN -->
        </div>
        <!-- ROW -->
      </div>
      <!-- MODAL DIALOG -->
    </div>
  </div>
</template>

<script>
const deepEqual = require("fast-deep-equal");
import { cloneDeep } from "lodash";
import { mapState, mapGetters, mapActions } from "vuex";
import moment from "moment";
import breakpoint from "@/mixins/index";
import {
  groupOverridingRulesWithGlobalRules,
  filterCumulativeRulesByTarget,
  removeOverriddenRules,
  filterRulesByPriority,
  saveRules,
  deleteRules,
  getRuleScopeOfferExclusions,
  applyTargetToRules,
  isCumulativeRuleGroupRelevantForOffer,
  isRtfoOrDrtfoRule,
} from "@/util/pricingRules";
import { rulesEnum } from "@/util/enums/rules";
import { costAdditionScope } from "@/util/enums/costAdditionScope";
import { isBlendedProduct, getBioProductId, getStraightProductId, getProductRangeIds } from "@/util/products";

import { createOffer, editOffer } from "@/api/offers";

import PriceBreakdown from "@/components/Offers/PriceBreakdown.vue";
import CostAdditions from "@/components/PricingEngine/CostAdditions.vue";
import { middayISODateFormat } from "@/util/filters";
import { deepCopy } from "../util/util";

import { costAdditionRulesForOffer } from "@/api/pricingRules";

export default {
  mixins: [breakpoint],
  data: function () {
    const validateSelection = (rule, value, callback) => {
      if (value == null || value === "" || !/\S/.test(value)) {
        callback(new Error(" "));
      } else {
        callback();
      }
    };
    const validateLiftingPeriodSelection = (rule, value, callback) => {
      if (value == null || !Array.isArray(value)) {
        callback(new Error(" "));
      } else if (value.length < 2) {
        callback(new Error(" "));
      } else {
        callback();
      }
    };
    const validateFuelGroupSelection = (rule, value, callback) => {
      if (this.fuelGroupSelected == null || value === "") {
        callback(new Error(" "));
      } else {
        callback();
      }
    };
    const validateTextInput = (rule, value, callback) => {
      if (value == null || value === "" || !/\S/.test(value)) {
        callback(new Error(" "));
      } else {
        callback();
      }
    };
    const validateTotalPriceInput = (rule, value, callback) => {
      if (this.mixin_formatStringToNumber(value) <= 0) {
        callback(new Error("Final price must be greater than 0"));
      } else {
        callback();
      }
    };
    const validateMinimumInput = (rule, value, callback) => {
      if (this.mixin_formatStringToNumber(value) <= 0) {
        callback(new Error("Minimum volume must be an integer greater than 0"));
      } else if (!this.isContractOffer) {
        if (
          value == null ||
          value === "" ||
          !/\S/.test(value) ||
          this.mixin_formatStringToNumber(value) > this.mixin_formatStringToNumber(this.offer.maximumAvailable)
        ) {
          callback(new Error("Minimum volume cannot be greater than max volume"));
        } else {
          callback();
        }
      } else if (this.isContractOffer) {
        if (
          this.mixin_formatStringToNumber(value) > this.mixin_formatStringToNumber(this.offer.contracts.totalQuantity)
        ) {
          callback(new Error("Minimum volume per order must be less than or equal to total contract volume"));
        } else {
          let errorFound = false;
          let validFrequencyQuantity = this.offer.contracts.frequencyQuantity.filter((month) => {
            if (moment(new Date(), "MM/YYYY").isBefore(moment(month.untilDate, "MM/YYYY"))) {
              return month;
            }
          });
          const sorted = [...validFrequencyQuantity].sort((a, b) => {
            return this.mixin_formatStringToNumber(a.quantity) - this.mixin_formatStringToNumber(b.quantity);
          });
          if (this.mixin_formatStringToNumber(value) > this.mixin_formatStringToNumber(sorted[0].quantity)) {
            errorFound = true;
          }
          if (errorFound) {
            callback(new Error("Minimum order volume must be less than or equal to the smallest monthly order volume"));
          } else {
            callback();
          }
        }
      }
    };
    const validateMonthlyQuantityInput = (rule, value, callback) => {
      const currMonthInitialStateIdx =
        this.offerInitialState.contracts &&
        this.offerInitialState.contracts.frequencyQuantity.findIndex((fq) =>
          moment(fq.untilDate).isSameOrAfter(moment(), "day")
        );
      const currMonthInitialState =
        this.offerInitialState.contracts &&
        this.offerInitialState.contracts.frequencyQuantity[currMonthInitialStateIdx];
      if (currMonthInitialState == null) {
        // If this is a newly added month, we can safely assume that the buyer has not bought any out of the specified volume
        callback();
      } else {
        /* Calculate bought volume for this month and make sure that current month's total volume does not fall below the volume already bought */
        const currMonthNewState = this.offer.contracts.frequencyQuantity.find((fq) =>
          moment(fq.untilDate).isSameOrAfter(moment(), "day")
        );
        const newMonthlyVolume = this.mixin_formatStringToNumber(currMonthNewState.quantity);
        const boughtVolume =
          this.mixin_formatStringToNumber(currMonthInitialState.quantity) -
          this.mixin_formatStringToNumber(currMonthInitialState.maximumAvailable);
        const volumeRemaining = newMonthlyVolume - boughtVolume;

        if (boughtVolume > newMonthlyVolume) {
          callback(
            new Error(
              `The buyer has already purchased ${this.$root.$options.filters.formatNumber(
                boughtVolume
              )} litres this month. Current Month Volume must be greater.`
            )
          );
        } else {
          callback();
        }
      }
    };
    const validateEndMonthSelection = (rule, value, callback) => {
      if (value == null || value === "" || !/\S/.test(value)) {
        callback(new Error(" "));
      } else if (moment(value, "DD/MM/YYYY") < moment()) {
        callback(new Error("Contract End Date can't be before today"));
      } else {
        callback();
      }
    };
    const validateMaximumInput = (rule, value, callback) => {
      if (
        value == null ||
        value === "" ||
        !/\S/.test(value) ||
        this.mixin_formatStringToNumber(value) < this.mixin_formatStringToNumber(this.offer.minimumOrderQuantity)
      ) {
        callback(new Error("Maximum volume cannot be less than Minimum volume"));
      } else {
        if (
          this.mixin_formatStringToNumber(this.offer.minimumOrderQuantity) > 0 &&
          this.mixin_formatStringToNumber(value) > this.mixin_formatStringToNumber(this.offer.minimumOrderQuantity)
        ) {
          this.$refs["ruleForm"].fields.find((f) => f.prop == "minimumOrderQuantity").clearValidate();
        }
        callback();
      }
    };
    const validatePaymentTermInput = (rule, value, callback) => {
      if (this.isPaymentTermOverridden) {
        if (value == null || value === "" || !/\S/.test(value)) {
          callback(new Error("Please enter a valid payment term"));
        } else if (Number(value) >= 100) {
          callback(new Error("Please enter a valid payment term"));
        } else {
          callback();
        }
      } else {
        callback();
      }
    };
    return {
      offerSpecificRules: [],
      saveOfferBtnLoading: false,
      validateMonthlyQuantityInput: validateMonthlyQuantityInput,
      pickerEndOptions: {
        disabledDate: this.disabledEndDates,
      },
      pickerStartOptions: {
        disabledDate: this.disabledStartDates,
      },
      updateRemainingVolume: false,
      serviceSelected: "",

      /* The following are for incremental search on terminal */
      showMatchesList: false,
      matchesFound: [],
      matchesListIndex: -1,

      /* The following are options used on input fields/dropdowns */
      showBioOptions: false,
      serviceTypeOptions: ["SPOT", "BUNKERING"],
      fuelMeasurementOptions: ["STANDARD", "AMBIENT"],
      collectionPeriodOptions: [-1, 0, 1, 2, 3, 7, 30],
      basePricingMethodOptions: ["D1", "D2", "TRACKING", "FIXED"],
      priceInputTypeOptions: ["PREMIUM", "TOTAL"],
      frequencyOptions: ["MONTHLY"],
      dates: {
        today: new Date(),
      },
      fuelGroupSelected: null,
      buyersList: [],
      brands: [],

      offer: {
        // The offer object to be submitted
        liftingPeriod: [],
        fuel: null,
        collectionTime: null,
        pricingMethod: null,
        fuel_measurement_id: null,
        terminal: "",
        minimumOrderQuantity: null,
        maximumAvailable: 0,
        premium: 0.0,
        totalPrice: 0.0,
        priceInputType: "PREMIUM",
        isContract: false,
        contracts: {
          buyer: null,
          dates: {
            start: "",
            end: "",
          },
          frequency: "MONTHLY",
          reference: "",
          totalQuantity: 0,
          frequencyQuantity: [],
          paymentTermOverride: null,
        },
        meta: null,
      },
      offerInitialState: "", // To store offer's initial state to be used on reset
      isCalculationAllowed: false, // To prevent computed property from calculating when not needed
      visitedOfferInputIds: [],
      usePaymentTermOverride: false,
      showIntegrationDetails: false,

      /* The following are relevant for confirmation window */
      showConfirmationWindow: false,
      confirmationMessage: "",
      offerPlaced: false,
      firstOfMonth: {
        days: [1],
      },
      lastOfMonth: {
        days: [-1],
      },

      /* Pricing Engine */
      usePricingEngine: false,
      rules: {},
      formRules: {
        fuelGroupSelected: [{ required: true, validator: validateFuelGroupSelection, trigger: ["change", "blur"] }],
        terminal: [{ required: true, validator: validateSelection, trigger: ["change", "blur"] }],
        fuel: [{ required: true, validator: validateSelection, trigger: ["change", "blur"] }],
        collectionTime: [{ required: true, validator: validateSelection, trigger: ["change", "blur"] }],
        pricingMethod: [{ required: true, validator: validateSelection, trigger: ["change", "blur"] }],
        fuel_measurement_id: [{ required: true, validator: validateSelection, trigger: ["change", "blur"] }],
        liftingPeriod: [{ required: true, validator: validateLiftingPeriodSelection, trigger: ["change", "blur"] }],
        minimumOrderQuantity: [{ required: true, validator: validateMinimumInput, trigger: ["change", "blur"] }],
        maximumAvailable: [{ required: true, validator: validateMaximumInput, trigger: ["change", "blur"] }],
        totalPrice: [{ required: true, validator: validateTotalPriceInput, trigger: ["change", "blur"] }],
        priceInputType: [{ required: true, validator: validateSelection, trigger: ["change", "blur"] }],
        contracts: {
          buyer: [{ required: true, validator: validateSelection, trigger: ["change", "blur"] }],
          dates: {
            start: [{ required: true, validator: validateSelection, trigger: ["change", "blur"] }],
            end: [{ required: true, validator: validateEndMonthSelection, trigger: ["change", "blur"] }],
          },
          reference: [{ required: true, validator: validateTextInput, trigger: "blur" }],
          frequencyQuantity: [],
          paymentTermOverride: [{ required: false, validator: validatePaymentTermInput, trigger: ["change", "blur"] }],
        },
        meta: {
          integration: {
            brand: [{ required: true, validator: validateSelection, trigger: ["change", "blur"] }],
            externalContractId: [{ required: true, validator: validateTextInput, trigger: ["change", "blur"] }],
          },
        },
      },
      originalRules: {
        list: [],
        populatedRuleKeys: [],
      },
      componentMetadata: {
        costAdditionsGlobal: {
          componentKey: "COST_ADDITIONS",
          title: "Cost Addition Rules",
          rules: [],
          areRulesValid: true,
        },
        costAdditionsBio: {
          componentKey: "BIO_COST_ADDITIONS",
          title: "Bio Rules",
          rules: [],
          areRulesValid: true,
        },
        costAdditionsTemperature: {
          componentKey: "TEMPERATURE",
          title: "Temperature Rules",
          rules: [],
          areRulesValid: true,
        },
        priceBreakdown: {
          rules: {},
          allRulesValid: true,
        },
      },
      isCumulativeRuleGroupRelevantForOffer,
      rulesEnum,
      pickerOptions: {
        disabledDate(time) {
          return time.getTime() < moment().startOf("day");
        },
      },
      costAdditionScopeGetter: costAdditionScope,
      originalBioRules: [],
      originalTempRules: [],
      originalCostAdditionRules: [],
      refreshKey: 0,
      loadingRuleData: false,
    };
  },
  components: {
    PriceBreakdown,
    CostAdditions,
  },
  created: async function () {
    this.loadingRuleData = true;
    if (this.isContractOffer) {
      const accountId = this.userLoggedIn.id;
      const getBuyers = await this.axios.get("/api/buyersList", { params: { accountId } });

      if (getBuyers.data.success) {
        getBuyers.data.payload.relationships.forEach((buyer) => {
          this.buyersList.push(buyer.buyer);
          this.buyersList.sort();
        });
      }

      this.offer.isContract = true;
      // Populate brand selection if there is any
      const { brands } = this.accountDetails;
      if (brands && brands.length > 0) {
        this.brands = [...brands];

        // Only populate integration details if this is creating a new contract/editing a contract with integration details previously populated
        if (!this.getEditingStatus || (this.getOfferToEdit.meta && this.getOfferToEdit.meta.integration)) {
          this.offer.meta = {
            integration: {
              brand: null,
            },
          };
          this.showIntegrationDetails = true;
        }
      }
    }

    if (this.getEditingStatus) {
      // Editing an offer and populate with the offer details
      await this.populateFieldsWithOfferDetails(this.getOfferToEdit);
    } else {
      if (this.isDuplicatingOffer) {
        // Duplicating an offer, so let's populate the offer details
        this.offerInitialState = cloneDeep(this.offer); // Assign the offer initial details now, as if we are creating a new offer
        await this.populateFieldsWithOfferDetails(this.offerToDuplicate);
      }

      /* Allow computed properties to do their work since we don't need to populate any data */
      this.isCalculationAllowed = true;
    }

    if (this.isPricingEngineEnabled) {
      this.usePricingEngine = true;
      await this.getOfferCostAdditions();
      await this.processRulesForOffer();
      await this.getApplicableRules();
    }
    if (!this.isDuplicatingOffer) {
      this.offerInitialState = cloneDeep(this.offer);
    }

    this.loadingRuleData = false;
  },
  updated() {
    $('[data-toggle="tooltip"]').tooltip();
  },
  computed: {
    ...mapState("fuelsType", ["fuels"]),
    ...mapState("sellerOffers", ["offerToDuplicate", "isDuplicatingOffer"]),
    ...mapState("login", ["userLoggedIn"]),
    ...mapState("inputCheckers", ["numeral", "numeralPositive", "naturalNumber"]),
    ...mapGetters("fuelsType", ["getFuelsType"]),
    ...mapGetters("livePrices", ["getPrices"]),
    ...mapGetters("sellerTabs", ["getTabSelected"]),
    ...mapGetters("sellerOffers", ["getOfferToEdit", "getEditingStatus", "offerInputIds"]),
    ...mapGetters("terminals", ["getTerminalChanges"]),
    ...mapGetters("metadata", ["productConfig", "blendedProductConfig", "networkConfig", "networks"]),
    ...mapGetters("login", ["isPricingEngineEnabled", "isSettlementPricingEnabled"]),
    ...mapGetters("itemRemoval", ["unsavedChangesRemovalConfirmed"]),
    ...mapGetters("accounts", ["accountDetails"]),
    getBrands() {
      return this.brands;
    },
    pricingMethodOptions() {
      if (this.serviceSelected === "SPOT" && this.isSettlementPricingEnabled && !this.isContractOffer) {
        return [...this.basePricingMethodOptions, "SETTLEMENT"];
      }
      return this.basePricingMethodOptions;
    },
    errorsFound: function () {
      const errors = [];

      if (this.hasDailySettlementPricesBeenSet && this.offer.totalPrice <= 0) {
        errors.push(this.createError("Final price must be greater than 0"));
      }
      return errors;
    },
    relevantErrors: function () {
      let errors = [];

      if (this.errorsFound && this.visitedOfferInputIds) {
        // Let's filter the errors to only include those related to visited input IDs
        errors = this.errorsFound.filter((error) =>
          error.offerInputIds.some((inputId) => this.visitedOfferInputIds.includes(inputId))
        );
      }

      return errors;
    },
    hasDailySettlementPricesBeenSet: {
      set: function () {},
      get: function () {
        if (this.getPrices.length < 1) {
          // this.offer.pricingMethod = 'FIXED'
          return false;
        } else {
          return true;
        }
      },
    },

    converter() {
      if (this.getOfferToEdit.isContract) {
        this.offer.contracts.frequencyQuantity = this.offer.contract.frequency_id;
      }
    },
    frequencyQtyTotalVol() {
      if (
        this.offer.contracts.dates.start &&
        this.offer.contracts.dates.end &&
        this.offer.contracts.frequency === "MONTHLY" &&
        this.offer.contracts.frequencyQuantity &&
        this.offer.contracts.frequencyQuantity.length > 0
      ) {
        const frequencyTotal = this.offer.contracts.frequencyQuantity.length;
        let monthTotal = 0;

        for (let i = 0; i < frequencyTotal; i++) {
          monthTotal = monthTotal + this.mixin_formatStringToNumber(this.offer.contracts.frequencyQuantity[i].quantity);
        }
        this.offer.contracts.totalQuantity = this.numberWithCommas(monthTotal);
      }
      return true;
    },

    frequencyQuantityPopulater() {
      if (
        this.offer.contracts.dates.start &&
        this.offer.contracts.dates.end &&
        this.offer.contracts.frequency === "MONTHLY"
      ) {
        const contractStart = moment(this.offer.contracts.dates.start, "DD/MM/YYYY");
        const contractEnd = moment(this.offer.contracts.dates.end, "DD/MM/YYYY");
        const monthsInContract = contractEnd.diff(contractStart, "months") + 1; // How many months between the start and end of the contract

        if (!this.getEditingStatus) {
          this.offer.contracts.frequencyQuantity = [];
        }

        /* Assign and populate new months into the contract */
        for (
          let i = this.getEditingStatus ? this.offer.contracts.frequencyQuantity.length : 0;
          i < monthsInContract;
          i++
        ) {
          // Each can have monthly splits, i.e. your contract may be from Jan 1st to Dec 31st, but each month has it's own limits on how much product can be bought
          const contractSegmentEndDate = moment(this.offer.contracts.dates.start, "DD/MM/YYYY")
            .add("months", i)
            .endOf("month");
          this.offer.contracts.frequencyQuantity.push(
            Object.assign(
              {},
              {
                untilDate: contractSegmentEndDate.toDate(),
                quantity: 0,
              }
            )
          );

          this.formRules.contracts.frequencyQuantity.push({
            quantity: [{ required: true, validator: this.validateMonthlyQuantityInput, trigger: ["change", "blur"] }],
          });
        }

        /* While editing, seller may choose to reduce the number of months in the contract after previously increasing it */
        if (this.offer.contracts.frequencyQuantity.length > monthsInContract) {
          this.offer.contracts.frequencyQuantity.splice(
            monthsInContract,
            this.offer.contracts.frequencyQuantity.length - monthsInContract
          ); // Remove months that seller just created earlier
        }
      }
    },
    isContractOffer: {
      get: function () {
        return this.getTabSelected && this.getTabSelected.type === "Contracts";
      },
    },
    fuelsList: {
      get: function () {
        const typeToMatch = this.getEditingStatus ? this.getOfferToEdit.storage_type_id : this.serviceSelected;
        let filteredFuels = [];
        switch (typeToMatch) {
          case "BUNKERING":
            filteredFuels = this.networks;
            break;
          case "SPOT":
            filteredFuels = this.fuels.filter((fuel) => fuel.type === typeToMatch);
            break;
        }
        return [...filteredFuels];
      },
    },
    referencePrice: {
      get: function () {
        if (this.offer.fuel && this.getPrices) {
          const fuelSelected = this.getPrices.find((fuel) => fuel.fuel_id == this.offer.fuel._id);

          if (fuelSelected) {
            /* Use flat price as reference price if pricing method is D1 or D2 */
            if (this.offer.pricingMethod == "D1" || this.offer.pricingMethod == "D2") {
              return fuelSelected.totalFixed;
            } else {
              /* Otherwise use live price as reference price */
              return fuelSelected.totalLive;
            }
          }
        } else {
          return 0;
        }
      },
    },
    bioPricingOptions: {
      get: function () {
        let fuelTypes = [];

        if (this.fuelGroupSelected && this.serviceSelected) {
          /* If we are on bunkering or selected fuel type is Diesel, then options should be:
           * 1. Diesel,
           * 2. Blended Diesel
           */
          if (this.serviceSelected === "BUNKERING" || this.fuelGroupSelected._id === this.productConfig.DieselId) {
            const diesel = this.fuels.find((fuel) => fuel._id === this.productConfig.DieselId);
            const blendedDiesel = this.fuels.find((fuel) => fuel._id === this.productConfig.BlendedDieselBioId);
            fuelTypes.push(diesel, blendedDiesel);
          } else if (this.fuelGroupSelected._id === this.productConfig.GasoilId) {
            /* If selected fuel type is Gasoil, then options should be:
             * 1. Gasoil,
             * 2. Blended Gasoil
             */
            const gasoil = this.fuels.find((fuel) => fuel._id === this.productConfig.GasoilId);
            const blendedGasoil = this.fuels.find((fuel) => fuel._id === this.productConfig.BlendedGasoilId);
            fuelTypes.push(gasoil, blendedGasoil);
          } else if (this.fuelGroupSelected._id === this.productConfig.KeroseneId) {
            /* If selected fuel type is Kerosene, then options should be:
             * only Kerosene
             */
            const kerosene = this.fuels.find((fuel) => fuel._id === this.productConfig.KeroseneId);
            fuelTypes.push(kerosene);
          } else if (this.fuelGroupSelected._id === this.productConfig.PetrolId) {
            const petrol = this.fuels.find((fuel) => fuel._id === this.productConfig.PetrolId);
            fuelTypes.push(petrol);
          } else if (this.fuelGroupSelected._id === this.productConfig.PetrolWithAdditiveId) {
            const PetrolWithAdditive = this.fuels.find((fuel) => fuel._id === this.productConfig.PetrolWithAdditiveId);
            fuelTypes.push(PetrolWithAdditive);
          } else if (this.fuelGroupSelected._id === this.productConfig.SU97Id) {
            const SU97 = this.fuels.find((fuel) => fuel._id === this.productConfig.SU97Id);
            fuelTypes.push(SU97);
          } else if (this.fuelGroupSelected._id === this.productConfig.SU99Id) {
            const SU99 = this.fuels.find((fuel) => fuel._id === this.productConfig.SU99Id);
            fuelTypes.push(SU99);
          } else if (this.fuelGroupSelected._id === this.productConfig.DieselWithAdditiveId) {
            const DieselWithAdditive = this.fuels.find((fuel) => fuel._id === this.productConfig.DieselWithAdditiveId);
            fuelTypes.push(DieselWithAdditive);
          } else if (this.fuelGroupSelected._id === this.productConfig.GasoilFame0Id) {
            const GasoilFame0 = this.fuels.find((fuel) => fuel._id === this.productConfig.GasoilFame0Id);
            fuelTypes.push(GasoilFame0);
          } else if (this.fuelGroupSelected._id === this.productConfig.HVOId) {
            const HVO = this.fuels.find((fuel) => fuel._id === this.productConfig.HVOId);
            fuelTypes.push(HVO);
          } else if (this.fuelGroupSelected._id === this.productConfig.MarineGasoilId) {
            const marineGasoil = this.fuels.find((fuel) => fuel._id === this.productConfig.MarineGasoilId);
            fuelTypes.push(marineGasoil);
          } else if (this.fuelGroupSelected._id === this.productConfig.HighBioMarineDieselId) {
            const highBioMarineDiesel = this.fuels.find(
              (fuel) => fuel._id === this.productConfig.HighBioMarineDieselId
            );
            fuelTypes.push(highBioMarineDiesel);
          } else if (this.fuelGroupSelected._id === this.productConfig.PremiumDieselId) {
            const premiumDiesel = this.fuels.find((fuel) => fuel._id === this.productConfig.PremiumDieselId);
            fuelTypes.push(premiumDiesel);
          } else {
            console.log("bioPricingOptions error, not defined: ", this.fuelGroupSelected);
          }
        }
        return fuelTypes;
      },
    },
    shouldBioOptionsBeShown: function () {
      // Bio pricing options should be shown on Bunkering/if selected fuel type is either Diesel or Gasoil
      if (this.fuelGroupSelected) {
        const {
          DieselId,
          BlendedDieselBioId,
          GasoilId,
          BlendedGasoilId,
          KeroseneId,
          PetrolId,
          PetrolWithAdditiveId,
          SU97Id,
          SU99Id,
          DieselWithAdditiveId,
          GasoilFame0Id,
          HVOId,
          MarineGasoilId,
          HighBioMarineDieselId,
          PremiumDieselId,
        } = this.productConfig;

        if (
          this.serviceSelected == "BUNKERING" ||
          this.fuelGroupSelected._id === GasoilId ||
          this.fuelGroupSelected._id === BlendedGasoilId ||
          this.fuelGroupSelected._id === DieselId ||
          this.fuelGroupSelected._id === BlendedDieselBioId
        ) {
          // Deselect bio options if base product has changed
          if (this.offer.fuel != null && !this.bioPricingOptions.find((option) => option._id == this.offer.fuel._id)) {
            this.offer.fuel = null;
          }
          return (this.showBioOptions = true);
        } else if (
          this.fuelGroupSelected._id == KeroseneId ||
          this.fuelGroupSelected._id === PetrolId ||
          this.fuelGroupSelected._id === PetrolWithAdditiveId ||
          this.fuelGroupSelected._id === SU97Id ||
          this.fuelGroupSelected._id === SU99Id ||
          this.fuelGroupSelected._id === DieselWithAdditiveId ||
          this.fuelGroupSelected._id === GasoilFame0Id ||
          this.fuelGroupSelected._id === HVOId ||
          this.fuelGroupSelected._id === MarineGasoilId ||
          this.fuelGroupSelected._id === HighBioMarineDieselId ||
          this.fuelGroupSelected._id === PremiumDieselId
        ) {
          this.offer.fuel = this.bioPricingOptions[0];
          return (this.showBioOptions = false);
        }
      }
    },
    disablePremiumInputForFixed: function () {
      if (this.offer.pricingMethod == "FIXED") {
        this.offer.priceInputType = "TOTAL";
      }
    },
    disableTotalInputForSettlement: function () {
      if (this.offer.pricingMethod == "SETTLEMENT") {
        this.offer.priceInputType = "PREMIUM";
      }
    },
    calculatePrices: function () {
      // Calculate premium/total price according to option selected
      if (this.isCalculationAllowed) {
        if (this.offer.priceInputType == "PREMIUM") {
          const totalPrice = this.referencePrice + Number(this.offer.premium);
          this.offer.totalPrice = (Number.isNaN(totalPrice) ? 0 : Number(totalPrice)).toFixed(2);
        } else {
          this.offer.premium = (
            this.hasDailySettlementPricesBeenSet
              ? Number(this.offer.totalPrice) - this.referencePrice
              : this.offer.premium
          ).toFixed(2);
        }
      }
    },
    selectD1D2CollectionDays: function () {
      if (this.offer.pricingMethod == "D1") {
        this.offer.collectionTime = 0;
      } else if (this.offer.pricingMethod == "D2") {
        this.offer.collectionTime = 1;
      }
    },
    smallestMonthlyFrequencyQuantity: {
      get: function () {
        if (
          this.offer.contracts.frequency !== "MONTHLY" ||
          !this.offer.contracts.frequencyQuantity ||
          this.offer.contracts.frequencyQuantity.length === 0
        )
          return 0;

        /* We only care about current and future months, so let's filter the array first, then calculate minimum quantity */
        const validMonths = this.offer.contracts.frequencyQuantity.filter((fq) =>
          moment(fq.untilDate).isSameOrAfter(moment(), "day")
        );
        return validMonths.reduce(
          (acc, { quantity }) =>
            this.mixin_formatStringToNumber(quantity) < Number(acc)
              ? this.mixin_formatStringToNumber(quantity)
              : Number(acc),
          this.mixin_formatStringToNumber(validMonths[0].quantity)
        );
      },
    },
    detailsChanged: function () {
      // Has any of the offer or rules changed?
      if (
        this.offer ||
        this.componentMetadata.costAdditionsGlobal.rules ||
        this.componentMetadata.costAdditionsBio.rules ||
        this.componentMetadata.costAdditionsTemperature.rules
      ) {
        // If we are editing, ensure that the offer's initial state have been populated
        if (!this.getEditingStatus || this.offerInitialState) return this.hasOfferOrRulesBeenAltered();
      }
    },
    isPaymentTermOverridden: {
      get: function () {
        return this.usePaymentTermOverride;
      },
      set: function (value) {
        if (!value) this.offer.contracts.paymentTermOverride = null; // Revert value to null if override is removed
        this.usePaymentTermOverride = value;
      },
    },
    minimumContractExpiryDatePage: function () {
      let minimumContractExpiryDate = null;

      if (this.offer.contracts) {
        // Minimum contract expiry date is set to the current contract's expiry date or the new contract's starting date
        minimumContractExpiryDate = new Date(
          this.getEditingStatus ? this.getOfferToEdit.contract.end : this.offer.contracts.dates.start
        );
        return {
          month: minimumContractExpiryDate.getMonth() + 1, // v-calendar counts month with 1-12, instead of Date's 0-11
          year: minimumContractExpiryDate.getFullYear(),
        };
      } else {
        return minimumContractExpiryDate;
      }
    },
  },
  watch: {
    shouldBioOptionsBeShown() {},
    disablePremiumInputForFixed() {},
    disableTotalInputForSettlement() {},
    calculatePrices() {},
    selectD1D2CollectionDays() {},
    frequencyQuantityPopulater() {},
    unsavedChangesRemovalConfirmed: {
      handler(isConfirmed) {
        if (isConfirmed) {
          // If user decides to not keep unsaved changes
          this.closeModal(true); // Close the modal
        }
      },
    },
    detailsChanged() {},
    "offer.priceInputType"(newVal) {
      if (newVal === "PREMIUM") {
        this.offer.premium = 0;
      }
    },
  },
  methods: {
    async fetchRules() {
      if (this.isPricingEngineEnabled) {
        this.loadingRuleData = true;
        this.usePricingEngine = true;
        await this.getOfferCostAdditions();
        await this.processRulesForOffer();
        await this.getApplicableRules();
        this.refreshKey++;
        this.loadingRuleData = false;
      }
    },
    async getApplicableRules() {
      if (
        this.fuelGroupSelected &&
        this.offer.fuel &&
        this.offer.priceInputType &&
        this.usePricingEngine &&
        Object.keys(this.rules).length > 0
      ) {
        // Get product IDs relevant to the selected product (e.g. Blended and Base products for Diesel/Gasoil)
        const productRangeIds = getProductRangeIds(
          this.productConfig,
          this.offer.fuel._id || this.fuelGroupSelected._id
        );

        // Map offer details
        let mappedOffer = {
          terminal_id: this.offer.terminal && this.offer.terminal._id,
          isContract: this.offer.isContract,
          fuel_measurement_id: this.offer.fuel_measurement_id,
          price_input_type_id: this.offer.priceInputType,
          type_id: this.offer.pricingMethod,
        };
        if (this.getEditingStatus) mappedOffer._id = this.getOfferToEdit._id;
        if (this.serviceSelected === "BUNKERING") mappedOffer.networkId = this.fuelGroupSelected._id;

        // Ensure that we allow ALL rules if we are creating a contract, but only include contract-specific rules on edit
        const includeOnlyContractSpecificRules = !this.isContractOffer || this.getEditingStatus;

        // Filter cumulative rules according to offer details and group all related rules (i.e. overriding rules with their overridden rules) together
        this.componentMetadata.costAdditionsGlobal.rules = this.getRelevantRulesAndReorder(
          this.rules.costAdditions,
          mappedOffer,
          productRangeIds,
          rulesEnum.eOfferExclusion,
          includeOnlyContractSpecificRules
        );
        this.componentMetadata.costAdditionsBio.rules = this.getRelevantRulesAndReorder(
          this.rules.bioRules,
          mappedOffer,
          productRangeIds,
          rulesEnum.eOfferExclusion,
          includeOnlyContractSpecificRules
        );
        this.componentMetadata.costAdditionsTemperature.rules = this.getRelevantRulesAndReorder(
          this.rules.temperatureRules,
          mappedOffer,
          productRangeIds,
          rulesEnum.eOfferExclusion,
          includeOnlyContractSpecificRules
        );
        this.assignOriginalRulesList();

        // Populate components with applicable cumulative rules and trigger mapping in the child components
        this.componentMetadata.priceBreakdown.rules = {
          costAdditions: this.componentMetadata.costAdditionsGlobal,
          bioRules: this.componentMetadata.costAdditionsBio,
          temperatureRules: this.componentMetadata.costAdditionsTemperature,
        };
      }
    },
    assignOriginalRulesList() {
      // we need to populate the original list of rules here -> since it already gets the relevant rules and order
      // -> we can assign them now but only once thats why we check if list is length 0
      // by default originalRules.list is 0 on the offer modal creation so this will only run once
      if (this.getEditingStatus && this.originalRules.list.length === 0) {
        const originalCostAdditionRules = deepCopy(this.componentMetadata.costAdditionsGlobal.rules);
        const originalBioRules = deepCopy(this.componentMetadata.costAdditionsBio.rules);
        const originalTemparatureRules = deepCopy(this.componentMetadata.costAdditionsTemperature.rules);
        this.originalRules.list = [...originalCostAdditionRules, ...originalBioRules, ...originalTemparatureRules];
      }
    },
    numberWithCommas(x) {
      return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    },
    formatValue($event, index) {
      this.offer.contracts.frequencyQuantity[index].quantity = $event.target._vCleave.getFormattedValue();
    },
    disabledEndDates(time) {
      const calendarDate = new Date(time.getTime());
      const calendarMonth = calendarDate.getMonth();
      calendarDate.setDate(calendarDate.getDate() + 1); // every time it adds a day thus it always misses the last day of the month as then its a new month
      if (this.offer.contracts.dates.start) {
        if (moment(time) > moment(this.offer.contracts.dates.start, "DD/MM/YYYY")) {
          return calendarDate.getMonth() === calendarMonth;
        } else {
          return true;
        }
      } else {
        return testDate.getMonth() === month;
      }
    },
    disabledStartDates(time) {
      if (this.offer.contracts.dates.end) {
        return moment(time) > moment(this.offer.contracts.dates.end, "DD/MM/YYYY") || new Date(time).getDate() > 1;
      } else {
        return new Date(time).getDate() > 1;
      }
    },
    mixin_formatStringToNumber(value) {
      if (value) {
        const value1 = value.toString().replace(/,/g, "");
        const value2 = parseFloat(parseFloat(value1).toFixed(2));
        return value2;
      } else {
        return 0;
      }
    },
    ...mapActions("sellerOffers", [
      "getAllOffersForUser",
      "chooseServiceType",
      "closeSpotModal",
      "closeContractModal",
      "saveNewOffer",
      "editingEnd",
      "updateSuccess",
      "setOfferDuplicationStatus",
    ]),
    showProductOrNetwork: function () {
      return this.serviceSelected === "BUNKERING" ? "Network" : "Product";
    },
    changeRemainingVolume(bool, value) {
      /* If the Maximum Volume by frequency is changed manually, reset the remaining volume, via boolean set in this function */
      if (bool === true) {
        this.updateRemainingVolume = true; // Mark as true IF remaining volume does not equal the total frequency volume
        this.offer.maximumAvailable = this.mixin_formatStringToNumber(value); // IF monthly FREQ was changed, the remaining volume will be defaulted back to the new FREQ value
      } else {
        this.updateRemainingVolume = false;
      }
    },
    liveTriggerSelection() {
      if (this.offer.pricingMethod === "TRACKING") {
        this.offer.contracts.frequency = "MONTHLY";
      }
    },
    closeModal: function (isConfirmed) {
      if (isConfirmed) {
        setTimeout(() => {
          $(this.$el).modal("hide");
          this.$store.dispatch("itemRemoval/confirmUnsavedChangesRemoval", false);

          if (!this.isContractOffer) {
            this.closeSpotModal();
          } else {
            this.closeContractModal();
          }

          this.editingEnd();
          this.setOfferDuplicationStatus(false);
        }, 500);
      } else {
        if (!this.hasOfferOrRulesBeenAltered()) {
          this.closeModal(true);
        } else {
          $(this.$el).modal("hide"); // Hide current modal

          // Show removal confirmation modal
          const payload = {
            item: { priority: this.$store.getters["itemRemoval/eChangesType"].Offer },
            type: this.$store.getters["itemRemoval/eRemoveItemType"].UnconfirmedChanges,
            showWindow: true,
          };
          this.$store.dispatch("itemRemoval/openConfirmationRemoval", payload);
        }
      }
    },
    populateFieldsWithOfferDetails: function (offerRef) {
      let fuelIdSelected = "";
      this.serviceSelected = offerRef.storage_type_id;

      /* Append the general required fields */
      this.offer.minimumOrderQuantity = this.numberWithCommas(offerRef.minimumOrderQuantity);
      this.offer.maximumAvailable = this.numberWithCommas(offerRef.maximumAvailable);
      this.offer.pricingMethod = offerRef.type_id;
      this.offer.priceInputType = offerRef.price_input_type_id;
      this.offer.fuel_measurement_id = offerRef.fuel_measurement_id;

      /* Select fuel id to fetch based on offer fuel type: UK/Keyfuels or Diesel or Gasoil or Kerosene */
      if (offerRef.storage_type_id == "BUNKERING") {
        // UK/Keyfuels
        this.offer.networkId = offerRef.networkId;

        fuelIdSelected = offerRef.networkId;
      } else if (offerRef.storage_type_id == "SPOT") {
        /* Append the required fields according to their storage type */
        this.offer.terminal = offerRef.terminal;
        this.offer.collectionTime = offerRef.collectionTime;
        if (offerRef.liftingPeriod) {
          this.offer.liftingPeriod[0] = new Date(offerRef.liftingPeriod[0]);
          this.offer.liftingPeriod[1] = new Date(offerRef.liftingPeriod[1]);
        }

        fuelIdSelected = getStraightProductId(this.productConfig, offerRef.fuel._id);
      }

      /* Append the fuel and bio pricing type */
      this.fuelGroupSelected =
        this.fuels.find((fuel) => fuel._id == fuelIdSelected) || this.networks.find((n) => n._id === fuelIdSelected);
      this.offer.fuel = offerRef.fuel;

      /* Append the price accordingly */
      if (offerRef.type_id === "FIXED") {
        this.offer.totalPrice = Number(offerRef.price).toFixed(2);
      } else {
        this.offer.premium = Number(offerRef.price).toFixed(2);
        const fuelSelected = this.getPrices.find((fuel) => fuel.fuel_id == offerRef.fuel._id);
        if (this.offer.pricingMethod === "TRACKING" && this.hasDailySettlementPricesBeenSet) {
          this.offer.totalPrice = Number(Number(offerRef.price) + Number(fuelSelected.totalLive)).toFixed(2);
        } else if (this.offer.pricingMethod !== "TRACKING" && this.hasDailySettlementPricesBeenSet) {
          this.offer.totalPrice = Number(Number(offerRef.price) + Number(fuelSelected.totalFixed)).toFixed(2);
        }
      }

      /* Append contract details if we are editing a contract offer */
      if (offerRef.isContract && this.getEditingStatus) {
        this.offer.contracts.buyer = this.buyersList.find(
          (buyer) => buyer.account_id == offerRef.contract.buyer_account_id
        );
        this.offer.contracts.dates.start = new Date(offerRef.contract.start);
        this.offer.contracts.dates.end = new Date(offerRef.contract.end);
        this.offer.contracts.frequency = offerRef.contract.frequency_id;
        this.offer.contracts.reference = offerRef.contract.reference;
        this.offer.contracts.totalQuantity = this.numberWithCommas(offerRef.contract.totalQuantity);

        // Assign payment term override if there is any
        if (typeof offerRef.contract.paymentTermOverride === "number") {
          this.isPaymentTermOverridden = true;
          this.offer.contracts.paymentTermOverride = offerRef.contract.paymentTermOverride;
        }
        if (offerRef.contract.frequency_quantity && offerRef.contract.frequency_quantity.length > 0)
          this.offer.contracts.frequencyQuantity = offerRef.contract.frequency_quantity.map((frequencyQuantity) => {
            const item = {
              untilDate: new Date(frequencyQuantity.until_date),
              quantity: this.numberWithCommas(frequencyQuantity.quantity),
              maximumAvailable: this.numberWithCommas(frequencyQuantity.maximumAvailable),
            };
            return item;
          });
      }

      /* Append offer meta if exists */
      if (offerRef.meta) {
        this.offer.meta = offerRef.meta;

        // Assign offer's integration metadata
        const { integration } = offerRef.meta;
        if (integration && integration.brand) {
          const selectedBrand = this.getBrands.find((brand) => brand && brand._id === integration.brand._id);
          if (selectedBrand) this.offer.meta.integration.brand = selectedBrand;
        }
      }

      /* After details have been populated, now we can allow computed properties to do their work */
      this.isCalculationAllowed = true;
    },
    async chooseServiceType(serviceType) {
      this.serviceSelected = serviceType;
      this.fuelGroupSelected = null;
      this.offer.terminal = "";
      this.offer.pricingMethod = "";
      this.offer.priceInputType = "PREMIUM";
      await this.refreshRules();
    },
    setPriceFormat: function () {
      this.offer.premium = Number(this.offer.premium).toFixed(2);
      this.offer.totalPrice = Number(this.offer.totalPrice).toFixed(2);
    },
    hasOfferOrRulesBeenAltered: function () {
      let status = false;
      const offerInitialStateCopy = cloneDeep(this.offerInitialState);
      const offerCurrentStateCopy = cloneDeep(this.offer);
      const rulesCurrentState = cloneDeep([
        ...this.componentMetadata.costAdditionsGlobal.rules,
        ...this.componentMetadata.costAdditionsBio.rules,
        ...this.componentMetadata.costAdditionsTemperature.rules,
      ]);

      const hasProductOrNetworkChanged =
        this.fuelGroupSelected &&
        ((this.serviceSelected === "SPOT" &&
          this.fuelGroupSelected._id !==
            getStraightProductId(
              this.productConfig,
              offerInitialStateCopy.fuel ? offerInitialStateCopy.fuel._id : null
            )) ||
          (this.serviceSelected === "BUNKERING" && this.fuelGroupSelected._id !== offerInitialStateCopy.networkId));

      // If the offer is live tracking/D1/D2 and uses premium input/pricing rules, no need to compare the total price
      if (offerCurrentStateCopy.pricingMethod != "FIXED" && offerCurrentStateCopy.priceInputType != "TOTAL") {
        if (this.getEditingStatus) {
          delete offerInitialStateCopy.totalPrice;
          delete offerCurrentStateCopy.totalPrice;
        }

        // Check whether any of the offer and/or rule details have been altered
        status =
          !deepEqual(offerCurrentStateCopy, offerInitialStateCopy) ||
          !deepEqual(this.originalRules.list, rulesCurrentState) ||
          hasProductOrNetworkChanged;
      } else {
        // Check whether any of the offer details, including total price, have been altered
        offerInitialStateCopy.totalPrice = Number(offerInitialStateCopy.totalPrice).toFixed(2);
        status = !deepEqual(offerCurrentStateCopy, offerInitialStateCopy) || hasProductOrNetworkChanged;
      }

      return status;
    },
    processRulesForOffer: function () {
      // Deep copy list of rules to prevent binding
      let costAdditions = cloneDeep(
        this.offerSpecificRules.filter(
          (r) =>
            !r.targets.some((t) => t.type === "ACCOUNT") &&
            !r.offerExclusions.includes(rulesEnum.eOfferExclusion.StandardMeasurement) &&
            r.action.targetAttribute !== "BIO_BLEND"
        )
      );
      let bioRules = cloneDeep(this.offerSpecificRules.filter((r) => r.action.targetAttribute === "BIO_BLEND"));
      let temperatureRules = cloneDeep(
        this.offerSpecificRules.filter((r) => r.offerExclusions.includes(rulesEnum.eOfferExclusion.StandardMeasurement))
      );

      costAdditions.forEach((element) => {
        element.action.value = this.$root.$options.filters.formatNumber(element.action.value);
      });
      bioRules.forEach((element) => {
        element.action.value = this.$root.$options.filters.formatNumber(element.action.value);
      });
      temperatureRules.forEach((element) => {
        element.action.value = this.$root.$options.filters.formatNumber(element.action.value);
      });

      this.rules.costAdditions = costAdditions;
      this.rules.bioRules = bioRules;
      this.rules.temperatureRules = temperatureRules;

      this.originalBioRules = bioRules;
      this.originalTempRules = temperatureRules;
      this.originalCostAdditionRules = costAdditions;
    },
    getRulesToSaveAndDelete: function (offerId) {
      let rulesToDelete = []; // To be populated with rules to delete
      let validRules = []; // To be populated with rules to save

      // Go through each component metadata to populate both lists above
      for (const rulesCategory in this.componentMetadata) {
        const rulesCategoryMetadata = cloneDeep(this.componentMetadata[rulesCategory]);

        // Only loop through components that are instances of CostAdditions
        if (rulesCategoryMetadata.componentKey) {
          for (const [idx, rule] of rulesCategoryMetadata.rules.entries()) {
            // If rule is in exclusion list due to being removed, then add rule to deletion list
            if (rule.isRemoved) {
              rulesToDelete.push(rule);
            } else if (!rule.isOverridden) {
              const ruleObject = cloneDeep(rule);

              // If this is a contract offer, make sure that we apply the created rules/modify the selected global rules to be applied specifically to the offer
              if (!this.getEditingStatus && this.isContractOffer && !isRtfoOrDrtfoRule(ruleObject)) {
                // Delete the unneeded properties to mark a new rule, if we are duplicating and modifying an existing rule
                if (ruleObject._id) {
                  const ruleKeysToDelete = ["_id", "created", "deleted", "updated", "accountId", "userId"];
                  for (const key of ruleKeysToDelete) {
                    delete ruleObject[key];
                  }
                }

                // Change the rule priority to OFFER
                ruleObject.priority = rulesEnum.ePriority.Offer;

                // Remove all contract offer exclusions
                const eOfferExclusion = rulesEnum.eOfferExclusion;
                const contractOfferExclusions = [
                  eOfferExclusion.LiveContract,
                  eOfferExclusion.FixedContract,
                  eOfferExclusion.D1Contract,
                  eOfferExclusion.D2Contract,
                  eOfferExclusion.SettlementContract,
                ];
                ruleObject.offerExclusions = ruleObject.offerExclusions.filter(
                  (exclusion) => !contractOfferExclusions.includes(exclusion)
                );
              }

              validRules.push(ruleObject);
            }
          }
        }
      }

      const nonOverriddenRules = removeOverriddenRules(validRules); // Remove all overridden rules to prevent them from being saved
      let rulesToSave = filterRulesByPriority(nonOverriddenRules, rulesEnum.ePriority.Offer); // Filter only OFFER specific rules
      rulesToSave = applyTargetToRules(rulesEnum.eTargetType.Offer, [offerId], rulesToSave); // Apply the current offer ID as a target in each rule

      for (let rule of rulesToSave) {
        // If this is a temperature rule, then apply the offer's straight product as a target in the rule
        const straightProductId = getStraightProductId(this.productConfig, this.offer.fuel._id);
        const temperatureRuleOfferExclusions = getRuleScopeOfferExclusions(
          this.costAdditionScopeGetter.Temperature,
          this.costAdditionScopeGetter,
          rulesEnum.eOfferExclusion
        );
        const isTemperatureRule = temperatureRuleOfferExclusions.every((exclusion) =>
          rule.offerExclusions.includes(exclusion)
        );
        if (isTemperatureRule) {
          rule = applyTargetToRules(
            rulesEnum.eTargetType.Product,
            [straightProductId, this.offer.fuel._id],
            [rule],
            true
          )[0];
        } else {
          // For non-temperature rules, apply the offer's fuel ID as a target in the rule
          rule = applyTargetToRules(rulesEnum.eTargetType.Product, [this.offer.fuel._id], [rule], true)[0];
        }
      }

      if (this.serviceSelected == "SPOT") {
        // Apply the current offer's terminal ID as a target in each rule
        rulesToSave = applyTargetToRules(rulesEnum.eTargetType.Terminal, [this.offer.terminal._id], rulesToSave, true);
      } else {
        // Apply the current offer's network ID as a target in each rule
        rulesToSave = applyTargetToRules(
          rulesEnum.eTargetType.Network,
          [this.fuelGroupSelected._id],
          rulesToSave,
          true
        );
      }

      return { rulesToSave, rulesToDelete };
    },
    async validateRules(componentRef) {
      return this.$refs[componentRef].validateRules();
    },
    async validateForm() {
      let costAdditionsTemperatureRulesValid = false;
      let costAdditionsBioRulesValid = false;
      let costAdditionsGlobalRulesValid = false;
      if (
        this.usePricingEngine &&
        this.offer.pricingMethod !== "FIXED" &&
        this.offer.priceInputType != "TOTAL" &&
        this.serviceSelected
      ) {
        if (
          this.isCumulativeRuleGroupRelevantForOffer(
            this.costAdditionScopeGetter,
            this.componentMetadata.costAdditionsTemperature.componentKey,
            this.offer,
            this.blendedProductConfig
          )
        ) {
          costAdditionsTemperatureRulesValid = await this.validateRules("cumulativeRuleGroupTemperature");
        } else {
          costAdditionsTemperatureRulesValid = true;
        }
        if (
          this.isCumulativeRuleGroupRelevantForOffer(
            this.costAdditionScopeGetter,
            this.componentMetadata.costAdditionsBio.componentKey,
            this.offer,
            this.blendedProductConfig
          )
        ) {
          costAdditionsBioRulesValid = await this.validateRules("cumulativeRuleGroupBio");
        } else {
          costAdditionsBioRulesValid = true;
        }
        if (
          this.isCumulativeRuleGroupRelevantForOffer(
            this.costAdditionScopeGetter,
            this.componentMetadata.costAdditionsGlobal.componentKey,
            this.offer,
            this.blendedProductConfig
          )
        ) {
          costAdditionsGlobalRulesValid = await this.validateRules("cumulativeRuleGroupGlobal");
        } else {
          costAdditionsGlobalRulesValid = true;
        }
      } else {
        costAdditionsTemperatureRulesValid = true;
        costAdditionsBioRulesValid = true;
        costAdditionsGlobalRulesValid = true;
      }
      this.$refs["ruleForm"].validate((valid) => {
        if (
          valid &&
          costAdditionsTemperatureRulesValid &&
          costAdditionsBioRulesValid &&
          costAdditionsGlobalRulesValid
        ) {
          this.saveOffer();
          return true;
        } else {
          return false;
        }
      });
    },
    saveOffer: async function () {
      this.saveOfferBtnLoading = true;
      let offerPrice = 0;

      if (this.hasDailySettlementPricesBeenSet) {
        if (this.offer.pricingMethod === "FIXED") {
          offerPrice = Number(this.offer.totalPrice);
        } else if (!this.usePricingEngine || this.offer.priceInputType === "TOTAL") {
          // Assign fixed price
          offerPrice = Number(this.offer.premium); // Assign set premium
        }
      }
      let offerToSend = {
        sellerUserId: this.userLoggedIn.userId,
        accountId: this.userLoggedIn.id,
        typeId: this.offer.pricingMethod,
        fuelId: this.offer.fuel._id,
        fuelMeasurementId: this.offer.fuel_measurement_id,
        price: offerPrice,
        priceInputTypeId: this.hasDailySettlementPricesBeenSet ? this.offer.priceInputType : null,
        storageTypeId: this.serviceSelected,
        quantity: this.isContractOffer
          ? this.mixin_formatStringToNumber(this.offer.contracts.totalQuantity)
          : this.mixin_formatStringToNumber(this.offer.maximumAvailable),
        minOrderQuantity: this.mixin_formatStringToNumber(this.offer.minimumOrderQuantity),
      };

      /* For 'SPOT' offer: add the terminal id and collection period
       * For 'BUNKERING' offer: assign the network id
       */
      if (this.serviceSelected == "SPOT") {
        offerToSend.terminalId = this.offer.terminal._id;
        offerToSend.collectionTime = this.offer.collectionTime;
        if (this.offer.collectionTime === -1) {
          offerToSend.liftingPeriodStart = middayISODateFormat(this.offer.liftingPeriod[0]);
          offerToSend.liftingPeriodEnd = middayISODateFormat(this.offer.liftingPeriod[1]);
        }
      } else {
        offerToSend.networkId = this.fuelGroupSelected._id;
      }

      /* If it is a contract offer, then add the additional relevant fields */
      if (this.isContractOffer) {
        (offerToSend.contractStartDate = moment(this.offer.contracts.dates.start, "DD/MM/YYYY")),
          (offerToSend.contractEndDate = moment(this.offer.contracts.dates.end, "DD/MM/YYYY")),
          (offerToSend.contractReference = this.offer.contracts.reference),
          (offerToSend.contractBuyerAccountId = this.offer.contracts.buyer.account_id),
          (offerToSend.contractFrequencyId = this.offer.contracts.frequency);
        if (this.isPaymentTermOverridden)
          offerToSend.contractPaymentTermOverride = this.offer.contracts.paymentTermOverride;
        if (this.offer.contracts.frequencyQuantity.length > 0) {
          for (let i = 0; i < this.offer.contracts.frequencyQuantity.length; i++) {
            // If this is a new month added, then we can just match maximum available with the specified quantity
            if (!this.offerInitialState.contracts.frequencyQuantity[i]) {
              this.offer.contracts.frequencyQuantity[i].maximumAvailable = this.mixin_formatStringToNumber(
                this.offer.contracts.frequencyQuantity[i].quantity
              );
              this.offer.contracts.frequencyQuantity[i].quantity = this.mixin_formatStringToNumber(
                this.offer.contracts.frequencyQuantity[i].quantity
              );
              /* If seller has changed a period's total volume, then recalculate remaining available volume */
            } else if (
              this.mixin_formatStringToNumber(this.offer.contracts.frequencyQuantity[i].quantity) !==
              this.mixin_formatStringToNumber(this.offerInitialState.contracts.frequencyQuantity[i].quantity)
            ) {
              const boughtVolume =
                this.mixin_formatStringToNumber(this.offerInitialState.contracts.frequencyQuantity[i].quantity) -
                this.mixin_formatStringToNumber(this.offerInitialState.contracts.frequencyQuantity[i].maximumAvailable);
              this.offer.contracts.frequencyQuantity[i].maximumAvailable =
                this.mixin_formatStringToNumber(this.offer.contracts.frequencyQuantity[i].quantity) - boughtVolume;
              this.offer.contracts.frequencyQuantity[i].quantity = this.mixin_formatStringToNumber(
                this.offer.contracts.frequencyQuantity[i].quantity
              );
            }
          }

          offerToSend.frequencyQuantity = this.offer.contracts.frequencyQuantity;
        }

        if (this.getEditingStatus)
          offerToSend.contractTotalQuantity = this.mixin_formatStringToNumber(this.offer.contracts.totalQuantity);

        if (this.offer.meta && this.offer.meta.integration) {
          const { brand } = this.offer.meta.integration;
          if (brand) {
            const brandCopy = cloneDeep(brand);
            delete brandCopy.name; // No need to save brand name
            this.offer.meta.integration.brand = brandCopy;
          }
          offerToSend.meta = this.offer.meta;
        }
      }

      try {
        let offerId;

        /* Decide whether to do a POST for new offer or a PUT for editing */
        if (!this.getEditingStatus) {
          const offer = await createOffer(offerToSend);
          offerId = offer._id;
        } else {
          offerId = this.getOfferToEdit._id;
          const offer = await editOffer({ offerId, offerToSend });
          if (!offer) {
            return this.createOfferModalError();
          }
        }

        // Get list of modified rules, then save and/or delete them
        if (this.usePricingEngine) {
          const { rulesToSave, rulesToDelete } = this.getRulesToSaveAndDelete(offerId);
          if (rulesToSave && rulesToSave.length > 0) {
            rulesToSave.forEach((rule) => {
              rule.action.value = Number((rule.action.value ?? 0).toString().replace(/,/g, "")).toFixed(2);
            });
          }
          await saveRules(rulesToSave);
          // Delete a list of rules that are redundant
          // IMPORTANT: Deletion should only be done if saving rules above is successful
          await deleteRules(rulesToDelete);
        }

        // Fetch updated offers list and rules and notify user that they have been saved
        await this.getAllOffersForUser();

        this.offerPlaced = true;
        this.confirmationMessage = "Your offer has been " + (this.getEditingStatus ? "edited" : "placed");
        this.showConfirmationWindow = true;
      } catch (error) {
        console.dir(error);
        // Notify user that saving offer and/or rules have failed
        this.createOfferModalError();
      }

      this.saveOfferBtnLoading = false;
    },
    createOfferModalError() {
      this.offerPlaced = false;
      this.confirmationMessage =
        "Sorry we are currently unable to " +
        (this.getEditingStatus ? "edit" : "place") +
        " your offer. Please try again later.";
      this.showConfirmationWindow = true;
    },
    monthIsDisabled(untilDate) {
      const now = new Date();
      const currentUntilDate = new Date(untilDate);
      if (now.getFullYear() > currentUntilDate.getFullYear()) return true;
      if (now.getFullYear() >= currentUntilDate.getFullYear() && now.getMonth() > currentUntilDate.getMonth())
        return true;
      return false;
    },
    createError: function (message, offerInputIds = [], contractMonthIndex = -1) {
      return {
        message,
        offerInputIds,
        contractMonthIndex,
      };
    },
    validateOfferInput: function (inputId) {
      const offerInputIds = this.offerInputIds;
      if (inputId === offerInputIds.Premium || inputId === offerInputIds.TotalPrice) this.setPriceFormat();
    },
    getRelevantRulesAndReorder(rules, offer, productRangeIds, eOfferExclusion, includeOnlyContractSpecificRules) {
      const filteredCostAdditionRules = filterCumulativeRulesByTarget(
        cloneDeep(rules || []),
        offer,
        productRangeIds,
        eOfferExclusion,
        includeOnlyContractSpecificRules
      );
      const groupedCostAdditionRules = groupOverridingRulesWithGlobalRules(filteredCostAdditionRules);
      return groupedCostAdditionRules.map((rule) => ({
        ...rule,
        isOverridden: groupedCostAdditionRules.some((r) => r.ruleToOverride === rule._id),
        isRemoved: false,
      }));
    },
    async getOfferCostAdditions() {
      const { fuel, terminal, networkId: network } = this.getEditingStatus ? this.getOfferToEdit : this.offer;
      let productId;
      const terminalId = terminal && terminal._id;
      if (fuel) {
        productId = isBlendedProduct(this.productConfig, fuel._id) ? getBioProductId(fuel._id) : fuel._id;
      }
      const networkId = network ? network : undefined;
      this.offerSpecificRules = await costAdditionRulesForOffer({ terminalId, productId, networkId });
      this.processRulesForOffer();
    },
    async refreshRules() {
      await this.fetchRules();
    },
  },
};
</script>
<style lang="scss" scoped>
@import "@/assets/scss/components/_modals.scss";
@import "@/assets/scss/components/_element.scss";
.invalid-value {
  label,
  span {
    color: map-get($theme-colors, t_red);
  }

  input,
  select {
    border-color: map-get($theme-colors, t_red);
  }
}
</style>
